import { BlockUI } from 'primereact/blockui';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import { ESTADO_CREADO_CODE, ESTADO_EN_INICIADO_CODE, ESTADO_FINALIZADO_CODE } from './data/custodiaDicctionary';
import { CamaraComponent } from './CamaraComponent';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { guardarEvidenciaCustodio, obtenerCustodiadoImagenes } from '../../services/Custodia/CustodiaService';

export const CargarEvidenciaCustodio = ({ origenId, tablaOrigen, onHide, custodiaId, estadoCustodia }) => {
    const [origen, setOrigen] = useState({ file: null, preview: null, description: '', registerId: null });
    const [destino, setDestino] = useState({ file: null, preview: null, description: '', registerId: null });
    const [isSaving, setIsSaving] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const toast = useRef(null);

    const generarPreview = (file, callback) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => callback(e.target.result);
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        if (origenId) {
            loadEvidencia(tablaOrigen, origenId);
        }

        return () => {
            setOrigen({ file: null, preview: null, description: '', registerId: null });
            setDestino({ file: null, preview: null, description: '', registerId: null });
        };
    }, [origenId, tablaOrigen]);

    const loadEvidencia = async (tablaOrigen, origenId) => {
        try {
            setIsLoading(true);

            const response = await obtenerCustodiadoImagenes(tablaOrigen, origenId);
            if (response.data?.length > 0) {
                const evidenciaOrigen = response.data.find((evidencia) => Number(evidencia.tipo) === 0);
                const evidenciaDestino = response.data.find((evidencia) => Number(evidencia.tipo) === 1);
                if (evidenciaOrigen) {
                    setOrigen({
                        ...origen,
                        preview: `data:image/png;base64,${evidenciaOrigen.imagen}`,
                        registerId: evidenciaOrigen.id
                    });
                }
                if (evidenciaDestino) {
                    setDestino({
                        ...destino,
                        preview: `data:image/png;base64,${evidenciaDestino.imagen}`,
                        description: evidenciaDestino.descripcion,
                        registerId: evidenciaDestino.id
                    });
                }
            }
        } catch (error) {
            console.error('Error al cargar la evidencia:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpload = async (type) => {
        const archivo = type === 0 ? origen?.file : destino?.file;
        if (!archivo) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Imagen requerida',
                detail: 'Tome o seleccione una foto antes de guardar.',
                life: 6000
            });
            return;
        }

        try {
            setIsSaving(true);

            const response = await guardarEvidenciaCustodio(type === 0 ? origen.registerId : destino.registerId, archivo, custodiaId, tablaOrigen, origenId, type, type === 1 ? destino.description : '');

            if (response && response.status === 200) {
                toast.current?.show({ severity: 'success', summary: 'Éxito', detail: 'Evidencia guardada', life: 7000 });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudo confirmar el guardado de la imagen. Intente nuevamente.',
                    life: 7000
                });
            }
        } catch (error) {
            console.error('Error al guardar la evidencia:', error);
            const detalle =
                error?.response?.data && typeof error.response.data === 'string'
                    ? error.response.data
                    : 'No se pudo guardar la imagen. Verifique su conexión e intente nuevamente.';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: detalle, life: 7000 });
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (isSaving || isLoading) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Espere',
                detail: 'Hay una carga en curso. Por favor espere a que termine.',
                life: 7000
            });
            return;
        }
        onHide();
    };

    return (
        <>
            <Toast ref={toast} baseZIndex={1000000} position="top-right" />
            {isSaving && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        zIndex: 100000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        cursor: 'wait'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <i className="pi pi-spin pi-spinner mb-3" style={{ fontSize: '4rem' }}></i>
                    <span style={{ fontSize: '1.3rem', fontWeight: 'bold', marginTop: '1rem' }}>Guardando imagen...</span>
                    <span style={{ fontSize: '1rem', marginTop: '0.5rem' }}>Por favor no cierre la ventana</span>
                </div>
            )}
            <Dialog
                header={
                    <div className="flex justify-content-between align-items-center w-full">
                        <span className="font-bold">Cargar Evidencia</span>
                        {!isSaving && !isLoading && (
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded p-button-plain"
                                onClick={handleClose}
                                aria-label="Cerrar"
                            />
                        )}
                    </div>
                }
                visible={!!origenId}
                onHide={handleClose}
                className="w-full sm:w-6"
                modal
                closable={false}
                closeOnEscape={!isSaving && !isLoading}
                dismissableMask={false}
            >
                <BlockUI blocked={isLoading} template={<i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>}>
                <p className="text-sm mb-4">Sube la imagen de origen y luego la de destino.</p>

                {/* ======================= */}
                {/*     ORIGEN               */}
                {/* ======================= */}
                <h4 className="font-semibold mb-2">1. Imagen de Origen</h4>

                <Card className="p-4 border-2 border-dashed surface-border mb-4 flex flex-column align-items-center justify-content-center">
                    <div className="flex flex-column ">
                        <div>
                            {estadoCustodia === ESTADO_CREADO_CODE && (
                                <CamaraComponent
                                    initCamera={origenId}
                                    onSuccessPhoto={(file) => {
                                        generarPreview(file, (preview) => {
                                            setOrigen((prev) => ({ ...prev, registerId: prev.registerId, file, preview }));
                                        });
                                    }}
                                />
                            )}
                        </div>
                        <div>
                            {origen.preview && (
                                <div className="w-full text-center">
                                    <img src={origen.preview} alt="Origen" className="w-full border-round mb-3" />
                                    {estadoCustodia === ESTADO_CREADO_CODE && (
                                        <Button
                                            label="Guardar Imagen"
                                            icon="pi pi-upload"
                                            className="w-full"
                                            onClick={() => handleUpload(0)}
                                            disabled={!origen.file || isSaving}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                <Divider />

                {/* ======================= */}
                {/*     DESTINO             */}
                {/* ======================= */}
                <h4 className="font-semibold mb-2">2. Imagen de Destino</h4>

                <Card className={classNames('p-4 border-2 border-dashed surface-border flex flex-column align-items-center justify-content-center', { 'opacity-40': !origen })}>
                    {!origen ? (
                        <p className="text-sm">Sube la imagen de origen primero</p>
                    ) : (
                        <div className="text-center">
                            <div className="flex flex-column gap-2 justify-content-center">
                                <div>
                                    {estadoCustodia === ESTADO_EN_INICIADO_CODE && (
                                        <CamaraComponent
                                            initCamera={origenId}
                                            onSuccessPhoto={(file) => {
                                                generarPreview(file, (preview) => {
                                                    setDestino((prev) => ({ ...prev, file, preview }));
                                                });
                                            }}
                                        />
                                    )}
                                </div>
                                <div>
                                    {destino.preview && (
                                        <div className="w-full text-center">
                                            <img src={destino.preview} alt="Destino" className="w-full border-round mb-3" />
                                            <InputText label="Descripción" value={destino.description} onChange={(e) => setDestino({ ...destino, description: e.target.value })} className="w-full mb-3" />

                                            {estadoCustodia === ESTADO_CREADO_CODE || estadoCustodia === ESTADO_FINALIZADO_CODE ? null : <div className="flex gap-2 justify-content-center"></div>}
                                            {estadoCustodia === ESTADO_EN_INICIADO_CODE && (
                                                <Button
                                                    label="Guardar Imagen"
                                                    icon="pi pi-upload"
                                                    className="w-full"
                                                    onClick={() => handleUpload(1)}
                                                    disabled={!destino.file || isSaving}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </Card>
                </BlockUI>
            </Dialog>
        </>
    );
};
